import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { Chip, Link, Typography } from '@mui/joy';
import { ArrowForwardRounded, VerifiedUserRounded } from '@mui-symbols-material/w300';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {WidgetBox} from "@dreebit-registry/vsm-microservice-components";
import { IOT_STATUS_COLOR_MAP } from '../../config/iotConnectionStatusConfig';

/** Placeholder shown wherever a value is not available yet (e.g. runtime). */
export const NO_DATA = '—';

/* ------------------------------------------------------------------ *
 *  Banner building blocks
 * ------------------------------------------------------------------ */

/** Hero banner: adaptive lead block + right-aligned KPI figures + a bottom actions row. */
export const HeroBanner = ({
    lead,
    kpis,
    actions,
}: {
    lead: React.ReactNode;
    kpis?: React.ReactNode;
    actions?: React.ReactNode;
}) => (
    <WidgetBox sx={{ mb: '16px' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '24px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flex: '1 1 340px' }}>
                {lead}
            </Box>
            {kpis && (
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    {kpis}
                </Box>
            )}
        </Box>
        {actions && (
            <Box
                sx={{
                    borderTop: '1px solid #eee',
                    mt: '14px',
                    pt: '12px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '12px',
                }}
            >
                {actions}
            </Box>
        )}
    </WidgetBox>
);

/** A single KPI figure (big value + caption) in the banner. Value skeletons while loading. */
export const HeroKpi = ({ value, label, loading }: { value: React.ReactNode; label: string; loading?: boolean }) => (
    <Box sx={{ textAlign: 'center', px: '16px' }}>
        <Typography level="titleExtraLarge" sx={{ color: '#333' }}>
            {loading ? <Skeleton width={32} sx={{ display: 'inline-block' }} /> : value}
        </Typography>
        <Typography level="bodySmall" sx={{ color: '#777' }} noWrap>
            {label}
        </Typography>
    </Box>
);

/** In/out-of-warranty chip derived from an asset's warrantyEndDate. Renders nothing when unknown. */
export const WarrantyChip = ({ warrantyEndDate }: { warrantyEndDate?: string | null }) => {
    const { t } = useTranslation();
    if (!warrantyEndDate) return null;
    const inWarranty = new Date() <= new Date(warrantyEndDate);
    const formatted = moment(warrantyEndDate).format('DD.MM.YYYY');
    return inWarranty ? (
        <Chip color="success" variant="soft" startDecorator={<VerifiedUserRounded sx={{ fontSize: 16 }} />}>
            {t('In warranty')} · {t('until {{date}}', { date: formatted })}
        </Chip>
    ) : (
        <Chip color="danger" variant="soft">
            {t('Out of warranty')} · {t('expired on {{date}}', { date: formatted })}
        </Chip>
    );
};

/* ------------------------------------------------------------------ *
 *  Feed building blocks
 * ------------------------------------------------------------------ */

/** Two-column responsive feed grid (single column when compact / narrow). */
export const FeedGrid = ({ compact, children }: { compact?: boolean; children: React.ReactNode }) => (
    <Box
        sx={{
            display: 'grid',
            // minmax(0, 1fr), not the bare 1fr (= minmax(auto, 1fr)): with `auto` a
            // column's minimum is its content's min-content width, so one long
            // non-wrapping line (e.g. an event message) widens the column and pushes
            // the other one off-screen. Truncation (noWrap) in the cards only works
            // when the columns keep their share of the available width.
            gridTemplateColumns: compact ? 'minmax(0, 1fr)' : { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
            gap: '16px',
            alignItems: 'start',
        }}
    >
        {children}
    </Box>
);

/** Vertical column inside the feed grid. */
export const FeedColumn = ({ children }: { children: React.ReactNode }) => (
    // minWidth 0 for the same reason as the grid's minmax(0, 1fr): the column must
    // never grow past its track because of a child's intrinsic width.
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>{children}</Box>
);

/**
 * "View all (n)" affordance shown at the bottom of list cards. Also used for other quiet
 * card-footer actions (e.g. "Create Sub-Asset") by swapping the trailing icon.
 */
export const ViewAllLink = ({
    label,
    onClick,
    icon,
    disabled,
}: {
    label: string;
    onClick?: () => void;
    /** Trailing icon; defaults to the forward arrow. */
    icon?: React.ReactElement<{ sx?: object }>;
    disabled?: boolean;
}) => (
    <Link
        component="button"
        color="primary"
        level="bodySmallBold"
        onClick={onClick}
        disabled={disabled}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', mt: '12px' }}
    >
        {label}
        {icon ? React.cloneElement(icon, { sx: { fontSize: 16 } }) : <ArrowForwardRounded sx={{ fontSize: 16 }} />}
    </Link>
);

/** Colored status dot for a list row (by IoT connection status). */
export const StatusDot = ({ status }: { status?: string | null }) => (
    <Box
        sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            flex: 'none',
            backgroundColor: (status && IOT_STATUS_COLOR_MAP[status]) || '#bbb',
        }}
    />
);

/** Compact list row: icon + title/meta + optional status dot + optional trailing text. */
export const HeroListRow = ({
    icon,
    iconSize = 28,
    iconPlain,
    title,
    meta,
    status,
    trailing,
    action,
    onClick,
}: {
    icon?: React.ReactNode;
    /** Edge length of the leading tile. Photos need more than the 28px an icon lives with. */
    iconSize?: number;
    /** Photo tile: white + hairline border instead of the tinted square an icon sits on. */
    iconPlain?: boolean;
    title: string;
    meta?: string;
    status?: string | null;
    trailing?: React.ReactNode;
    /** Optional trailing control (e.g. a button); its own click should stopPropagation. */
    action?: React.ReactNode;
    onClick?: () => void;
}) => (
    <Box
        onClick={onClick}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            py: '9px',
            borderTop: '1px solid #eee',
            cursor: onClick ? 'pointer' : 'default',
        }}
    >
        {icon && (
            <Box
                sx={{
                    width: iconSize,
                    height: iconSize,
                    borderRadius: '6px',
                    flex: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: iconPlain ? '#fff' : '#f4f4f4',
                    border: iconPlain ? '1px solid #ececec' : undefined,
                    color: '#666',
                    '& svg': { fontSize: 17 },
                }}
            >
                {icon}
            </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography level="bodyBold" sx={{ color: '#333' }} noWrap>
                {title}
            </Typography>
            {meta && (
                <Typography level="bodySmall" sx={{ color: '#777' }} noWrap>
                    {meta}
                </Typography>
            )}
        </Box>
        {status && <StatusDot status={status} />}
        {action}
        {trailing && (
            <Typography level="bodySmall" sx={{ color: '#777', whiteSpace: 'nowrap' }}>
                {trailing}
            </Typography>
        )}
    </Box>
);

/** Icon size of every dashboard card header — the customer/service cards' size. */
export const HERO_CARD_ICON_SIZE = 26;

/**
 * Header row of every asset/system dashboard card: icon + title, optional
 * action and count on the right. The row has a fixed height, so the large count
 * never pushes icon and title down — every icon sits at the same offset from
 * the card's top-left corner, with or without a count.
 */
export const HeroCardHeader = ({
    title,
    icon,
    count,
    headerAction,
    dense,
}: {
    title: string;
    icon?: React.ReactNode;
    count?: React.ReactNode;
    /** Small control (e.g. a "+" icon button) shown right of the title, before the count. */
    headerAction?: React.ReactNode;
    /** Smaller count for narrow layouts; icon and title stay the same. */
    dense?: boolean;
}) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: `${HERO_CARD_ICON_SIZE}px`, minWidth: 0 }}>
        {icon && (
            <Box sx={{
                color: '#333',
                display: 'flex',
                flexShrink: 0,
                '& svg': { fontSize: HERO_CARD_ICON_SIZE, width: HERO_CARD_ICON_SIZE, height: HERO_CARD_ICON_SIZE },
            }}>
                {icon}
            </Box>
        )}
        <Typography level="titleMediumLight" noWrap sx={{ color: '#333' }}>
            {title}
        </Typography>
        {headerAction && <Box sx={{ ml: 'auto', display: 'flex' }}>{headerAction}</Box>}
        {count != null && (
            <Typography
                level={dense ? 'titleLarge' : 'titleExtraLarge'}
                // lineHeight 1: the figure overhangs the fixed-height row
                // symmetrically instead of growing it
                sx={{ ml: headerAction ? 0 : 'auto', color: '#333', lineHeight: 1 }}
            >
                {count}
            </Typography>
        )}
    </Box>
);

/** Space between a card header and its content. */
export const HERO_CARD_CONTENT_GAP = '12px';

/** List card: header (icon + title + optional action + count) + rows + optional "view all". */
export const HeroListCard = ({
    title,
    icon,
    count,
    headerAction,
    viewAll,
    onViewAll,
    dense,
    children,
}: {
    title: string;
    icon?: React.ReactNode;
    count?: React.ReactNode;
    /** Small control (e.g. a "+" icon button) shown right of the title, before the count. */
    headerAction?: React.ReactNode;
    viewAll?: string;
    onViewAll?: () => void;
    dense?: boolean;
    children: React.ReactNode;
}) => (
    <WidgetBox>
        <HeroCardHeader title={title} icon={icon} count={count} headerAction={headerAction} dense={dense} />
        <Box sx={{ mt: HERO_CARD_CONTENT_GAP }}>{children}</Box>
        {viewAll && <ViewAllLink label={viewAll} onClick={onViewAll} />}
    </WidgetBox>
);
